import { Dialog } from "../ui/framework/dialog";
import Translator from "./translator";

const OFFLINE_CHECK_PERIOD = 5000;

export default class ReminderDialog {
    private readonly title: string;
    private readonly getMessage: () => string;
    private readonly onBeforeOpen;
    private readonly translator: Translator;
    private readonly canOpen: () => boolean;
    private timeout: number;
    private openingTimer;
    private startDate: number | null;
    private dialog: Dialog | null;
    private callback;

    /**
     * Creates an instance of the ReminderDialog class.
     *
     * @param translator - An instance of the Translator class for localizing dialog messages.
     * @param isUserNightMode - A flag indicating whether the user is in night mode.
     * @param timeout - The timeout period in milliseconds after which the dialog should appear.
     * @param onBeforeOpen - A callback function that is called before the dialog is opened.
     * @param title - The title of the dialog.
     * @param getMessage - A function that returns the message to be displayed in the dialog.
     * @param canOpen - A function that returns a boolean indicating whether the dialog can be opened.
     */
    constructor(
        translator: Translator,
        private readonly isUserNightMode: boolean,
        timeout: number,
        onBeforeOpen: () => void,
        title: string,
        getMessage: () => string,
        canOpen: () => boolean
    ) {
        this.translator = translator;
        this.timeout = timeout;
        this.onBeforeOpen = onBeforeOpen;
        this.getMessage = getMessage;
        this.title = title;
        this.canOpen = canOpen;
    }

    /**
     * Begins the timer that opens the ReminderDialog
     *      *
     * @param callback? - A callback function that is called after the dialog is opened or if the dialog cannot be opened.
     */
    open(callback?) {
        this.startDate = Date.now();
        this.callback = callback;
        this.scheduleCheck();
    }

    /**
     * Checks if the background conditions have changed and determines whether to display the dialog.
     *
     * @returns `true` if the dialog is displayed, `false` otherwise.
     */
    backgroundChanged(): boolean {
        if (this.startDate) {
            let timePassed = Date.now() - this.startDate;
            if (timePassed >= this.timeout) {
                clearTimeout(this.openingTimer);
                this.openingTimer = null;

                this.onTimerCallback();
                return true;
            }
        }
        return false;
    }

    /**
     * Checks if the reminder dialog is in the process of opening.
     *
     * @returns `true` if the dialog is opening, `false` otherwise.
     */
    isOpening(): boolean {
        return !!this.openingTimer;
    }

    /**
     * Closes the reminder dialog and clears any existing timers.
     */
    close() {
        if (this.openingTimer) {
            clearTimeout(this.openingTimer);
            this.openingTimer = null;
            this.startDate = null;
            this.callback = null;
        }

        this.dialog && this.dialog.remove();
    }

    /**
     * Sets the timeout period for the reminder dialog.
     *
     * @param timeout - The new timeout period in milliseconds.
     */
    setTimeout(timeout: number) {
        this.timeout = timeout;
    }

    /**
     * Schedules the next check for displaying the reminder dialog.
     */
    private scheduleCheck() {
        let passed = this.startDate ? (Date.now() - this.startDate) : 0;
        let remain = passed < this.timeout ? (this.timeout - passed) : 0;
        let nextTimeout = remain > 0 && remain < OFFLINE_CHECK_PERIOD ? remain : Math.min(OFFLINE_CHECK_PERIOD, this.timeout);
        this.openingTimer = setTimeout(() => {
            this.openingTimer = null;
            let richedTimeout = this.backgroundChanged();
            !richedTimeout && this.scheduleCheck();
        }, nextTimeout);
    }

    /**
     * Callback function that is called when the timer for displaying the reminder dialog expires.
     */
    private onTimerCallback() {
        let message = this.getMessage();
        if (!message || !this.canOpen()){
            this.callback && this.callback();
            this.callback = null;
            this.startDate = null;
            return;
        }
        this.dialog = new Dialog(null, message, this.translator, this.title, [{
            text: "OK",
            callback: this.close.bind(this)
        }], {
            isNightMode: this.isUserNightMode
        });

        this.onBeforeOpen();
        void this.dialog.open().then(() => {
            this.dialog && this.dialog.onRemove(() => {
                setTimeout(() => this.dialog = null, 1);
            });
        });
        this.callback && this.callback();
        this.callback = null;
        this.startDate = null;
    }
}